import React from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Box,
  Typography,
  Divider,
  Chip,
  Tooltip,
  IconButton
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Person as PersonIcon,
  Add as AddIcon,
  EmojiEvents as EmojiEventsIcon,
  Settings as SettingsIcon,
  Security as SecurityIcon,
  AdminPanelSettings as AdminIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import logoSvg from '../../assets/logo.svg';
import iconSvg from '../../assets/icon.svg';

const drawerWidth = 280;
const collapsedDrawerWidth = 72;

const Sidebar = ({ open, onToggle, mobileOpen, onMobileToggle }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const handleNavigation = (path) => {
    navigate(path);
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  // Base menu items
  const baseMenuItems = [
    {
      text: 'Fraud Records',
      icon: <SecurityIcon />,
      path: '/fraud-records'
    },
    {
      text: 'New Record',
      icon: <AddIcon />,
      path: '/fraud-records/new'
    },
    // {
    //   text: 'Fighters Board',
    //   icon: <EmojiEventsIcon />,
    //   path: '/fighters-board'
    // },
    {
      text: 'Settings',
      icon: <SettingsIcon />,
      path: '/settings'
    }
  ];

  // Add admin menu item if user is admin
  const menuItems = user?.role === 'admin' 
    ? [
        ...baseMenuItems.slice(0, 2), // Fraud Records, New Record
        {
          text: 'Admin',
          icon: <AdminIcon />,
          path: '/admin'
        },
        ...baseMenuItems.slice(2) // Settings
      ]
    : baseMenuItems;

  return (
    <>
      {/* Desktop Sidebar - Persistent */}
      <Drawer
        variant="permanent"
        open={open}
        sx={{
          display: { xs: 'none', md: 'block' },
          width: open ? drawerWidth : collapsedDrawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: open ? drawerWidth : collapsedDrawerWidth,
            boxSizing: 'border-box',
            background: open ? '#1f1f1f' : '#24252a',
            borderRight: 'none',
            transition: 'width 0.3s ease, background 0.3s ease',
            overflowX: 'hidden',
          },
        }}
      >
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: open ? 'space-between' : 'center' }}>
        {open ? (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <img 
                src={logoSvg} 
                alt="Biocatch Logo" 
                style={{ 
                  height: 40,
                  width: 'auto',
                  maxWidth: 180
                }} 
              />
            </Box>
          </>
        ) : (
          <img 
            src={iconSvg} 
            alt="Biocatch Icon" 
            style={{ 
              height: 32,
              width: 32
            }} 
          />
        )}
      </Box>
      
      {/* Toggle Button - positioned at the edge of sidebar */}
      <IconButton
        onClick={onToggle}
        sx={{
          position: 'absolute',
          right: -5,
          top: '50%',
          transform: 'translateY(-50%)',
          backgroundColor: '#2a2a2a',
          color: '#ffffff',
          width: 24,
          height: 24,
          zIndex: 1300,
          border: '1px solid #444',
          display: { xs: 'none', md: 'flex' }, // Only show on desktop
          '&:hover': {
            backgroundColor: '#3a3a3a',
          },
        }}
      >
        {open ? <ChevronLeftIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
      </IconButton>

      {open && <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)' }} />}

      <List sx={{ px: 2, py: 1 }}>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding sx={{ mb: 1 }}>
            <Tooltip title={open ? '' : item.text} placement="right">
              <ListItemButton
                onClick={() => handleNavigation(item.path)}
                sx={{
                  borderRadius: 2,
                  justifyContent: open ? 'initial' : 'center',
                  px: 2.5,
                  background: isActive(item.path) 
                    ? 'rgba(255, 255, 255, 0.16)'
                    : 'transparent',
                  '&:hover': {
                    background: isActive(item.path)
                      ? 'rgba(255, 255, 255, 0.24)'
                      : 'rgba(255, 255, 255, 0.08)',
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    color: isActive(item.path) ? 'primary.main' : 'text.secondary',
                    minWidth: 0,
                    mr: open ? 3 : 'auto',
                    justifyContent: 'center',
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  sx={{
                    opacity: open ? 1 : 0,
                    '& .MuiListItemText-primary': {
                      color: isActive(item.path) ? '#ffffff' : '#b0b0b0',
                      fontWeight: isActive(item.path) ? 600 : 400,
                    },
                  }}
                />
              </ListItemButton>
            </Tooltip>
          </ListItem>
        ))}
      </List>

      <Box sx={{ mt: 'auto', p: 2 }}>
        <Divider sx={{ borderColor: '#333333', mb: 2 }} />
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="caption" sx={{ color: '#666666' }}>
            Version 1.0.0
          </Typography>
        </Box>
      </Box>
    </Drawer>

    {/* Mobile Sidebar - Temporary Drawer */}
    <Drawer
      variant="temporary"
      open={mobileOpen}
      onClose={onMobileToggle}
      ModalProps={{
        keepMounted: true, // Better mobile performance
      }}
      sx={{
        display: { xs: 'block', md: 'none' },
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          background: '#1f1f1f',
          borderRight: 'none',
        },
      }}
    >
      {/* Same content as desktop but for mobile */}
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', paddingLeft:30 }}>
          <img 
            src={logoSvg} 
            alt="Biocatch Logo" 
            style={{ 
              height: 40,
              width: 'auto',
              maxWidth: 180
            }} 
          />
        </Box>
        <IconButton onClick={onMobileToggle}>
          <ChevronLeftIcon />
        </IconButton>
      </Box>
      
      <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)' }} />

      <List sx={{ px: 2, py: 1 }}>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding sx={{ mb: 1 }}>
            <ListItemButton
              onClick={() => {
                handleNavigation(item.path);
                onMobileToggle(); // Close mobile sidebar after navigation
              }}
              sx={{
                borderRadius: 2,
                backgroundColor: isActive(item.path) ? 'rgba(106, 70, 193, 0.3)' : 'transparent',
                color: isActive(item.path) ? '#ffffff' : '#b0b0b0',
                '&:hover': {
                  backgroundColor: isActive(item.path) ? 'rgba(106, 70, 193, 0.4)' : 'rgba(255, 255, 255, 0.05)',
                  color: '#ffffff',
                },
                transition: 'all 0.2s ease',
              }}
            >
              <ListItemIcon sx={{ 
                color: 'inherit',
                minWidth: 40
              }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      <Box sx={{ 
        mt: 'auto', 
        p: 2,
        borderTop: '1px solid rgba(255, 255, 255, 0.1)'
      }}>
        <Chip
          icon={<PersonIcon />}
          label={user?.role === 'admin' ? 'Admin' : 'Scam Fighter'}
          variant="outlined"
          size="small"
          sx={{
            color: user?.role === 'admin' ? '#ffa500' : '#00ff88',
            borderColor: user?.role === 'admin' ? '#ffa500' : '#00ff88',
            mb: 2,
            '& .MuiChip-icon': {
              color: user?.role === 'admin' ? '#ffa500' : '#00ff88'
            }
          }}
        />
        <Divider sx={{ borderColor: '#333333', mb: 2 }} />
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="caption" sx={{ color: '#666666' }}>
            Version 1.0.0
          </Typography>
        </Box>
      </Box>
    </Drawer>
    </>
  );
};

export default Sidebar;
